import React, { useState, useEffect } from 'react';
import { InventarioService } from '../../services/administration/inventarioService';
import { Stock } from '../../types/administracion';
import { getCurrentUserInfo, getRepositories } from '../../services/storage';
import ConfirmationModal from '../common/ConfirmationModal';
import { getLocalISODate } from '../../utils/dateHelper';

interface InventarioConsumptionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface ConsumoFormData {
  fecha: string;
  stock_id: string;
  cantidad: number | null;
  ubicacion_id?: string;
  descripcion: string;
}

export const InventarioConsumptionForm: React.FC<InventarioConsumptionFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<ConsumoFormData>({
    fecha: getLocalISODate(),
    stock_id: '',
    cantidad: null,
    ubicacion_id: undefined,
    descripcion: ''
  });

  const [stocks, setStocks] = useState<Stock[]>([]);
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStocks, setLoadingStocks] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [empresaId, setEmpresaId] = useState<string>('');
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadStocks();
  }, []);

  useEffect(() => {
    if (formData.stock_id) {
      const stock = stocks.find(s => s.id === formData.stock_id);
      setSelectedStock(stock || null);
    } else {
      setSelectedStock(null);
    }
  }, [formData.stock_id, stocks]);

  // Load empresa_id from profiles, fallback to first available empresa
  useEffect(() => {
    const loadEmpresaId = async () => {
      try {
        const currentUser = await getCurrentUserInfo();
        let empresaIdToUse = currentUser?.empresaId;

        // If no empresa in profile, try to get first available empresa
        if (!empresaIdToUse) {
          const { empresa } = getRepositories();
          const empresas = await empresa.getAll();
          if (empresas.length > 0) {
            empresaIdToUse = empresas[0].id;
            console.log('Using first available empresa:', empresas[0].nombre, empresaIdToUse);
          }
        }

        if (empresaIdToUse) {
          setEmpresaId(empresaIdToUse);
        } else {
          console.error('No active empresa found in profiles or empresas table');
          setError('No se encontró una empresa activa. Creá una empresa primero.');
        }
      } catch (err) {
        console.error('Error loading empresa_id:', err);
        setError('Error al cargar la información de la empresa.');
      }
    };

    loadEmpresaId();
  }, []);

  const loadStocks = async () => {
    try {
      setLoadingStocks(true);
      const availableStocks = await InventarioService.obtenerStocksDisponibles();
      setStocks(availableStocks);
    } catch (err: any) {
      console.error('Error cargando stocks:', err);
      setError('Error al cargar el inventario disponible');
    } finally {
      setLoadingStocks(false);
    }
  };

  // Use reusable validation from InventarioService
  const validateConsumption = async (stockId: string, cantidad: number, fecha: string): Promise<{ isValid: boolean; errorMessage?: string }> => {
    try {
      // Use the reusable FIFO validation function
      const validationResult = await InventarioService.validateStockAvailability(stockId, cantidad, fecha);
      return validationResult;
    } catch (error) {
      console.error('Error validating consumption:', error);
      return {
        isValid: false,
        errorMessage: 'Error al validar la disponibilidad de inventario.'
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.stock_id) throw new Error('Debe seleccionar un producto de inventario');
      if (formData.cantidad === null || formData.cantidad <= 0) throw new Error('La cantidad debe ser mayor a 0');

      // Validate stock availability on the selected date using reusable FIFO validation
      const validationResult = await validateConsumption(formData.stock_id, formData.cantidad, formData.fecha);
      if (!validationResult.isValid) {
        setErrorMessage(validationResult.errorMessage || `No hay suficiente inventario disponible para esta cantidad en la fecha seleccionada.`);
        setErrorModalOpen(true);
        return;
      }

      // Check current stock availability as well
      if (selectedStock && formData.cantidad > selectedStock.cantidad) {
        setErrorMessage(`Inventario insuficiente. Disponible: ${selectedStock.cantidad} ${selectedStock.unidad_medida}`);
        setErrorModalOpen(true);
        return;
      }

      await InventarioService.registrarConsumo({
        fecha_consumo: formData.fecha,
        stock_id: formData.stock_id,
        cantidad: formData.cantidad,
        ubicacion_id: formData.ubicacion_id,
        descripcion: formData.descripcion.trim() || undefined,
        empresaId: empresaId,
        userId: undefined
      });

      setFormData({
        fecha: getLocalISODate(),
        stock_id: '',
        cantidad: null,
        ubicacion_id: undefined,
        descripcion: ''
      });

      await loadStocks();

      if (onSuccess) onSuccess();
    } catch (err: any) {
      console.error('Error registrando consumo:', err);
      setErrorMessage(err.message || 'Error al registrar el consumo');
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-paper rounded-xl shadow-card">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ink">Usar Inventario</h2>
        <p className="text-sage text-sm">Registre el consumo de materiales o insumos del inventario</p>
      </div>

      {error && (
        <div className="border-l-4 border-danger bg-danger/10 p-4 mb-6 rounded-r">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-danger">⚠️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-danger">{error}</p>
            </div>
          </div>
        </div>
      )}

      {loadingStocks ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-danger mx-auto mb-4"></div>
          <p className="text-sage">Cargando inventario...</p>
        </div>
      ) : stocks.length === 0 ? (
        <div className="border border-warning/30 bg-warning/10 rounded-lg p-6 text-center">
          <span className="text-4xl block mb-3">📦</span>
          <h3 className="text-lg font-medium text-warning mb-2">Sin Inventario Disponible</h3>
          <p className="text-warning">No hay productos en el inventario para consumir. Registre una compra primero.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 1. Select Stock */}
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              ¿Qué vas a usar? <span className="text-danger">*</span>
            </label>
            <select
              className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink text-lg focus:border-danger focus:outline-none focus:ring-2 focus:ring-danger/20"
              value={formData.stock_id}
              onChange={(e) => setFormData({ ...formData, stock_id: e.target.value, cantidad: 0 })}
              required
              autoFocus
            >
              <option value="">Elegí un ítem...</option>
              {stocks.map((stock) => (
                <option key={stock.id} value={stock.id}>
                  {stock.nombre} ({stock.cantidad} {stock.unidad_medida})
                </option>
              ))}
            </select>
          </div>

          {/* Selected Stock Info */}
          {selectedStock && (
            <div className="border border-info/30 bg-info/10 rounded-lg p-4 flex justify-between items-center">
              <div>
                <p className="text-sm text-info font-medium">Disponible</p>
                <p className="text-2xl font-bold text-info">
                  {selectedStock.cantidad} <span className="text-base font-normal text-info">{selectedStock.unidad_medida}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-info font-medium">Categoría</p>
                <p className="text-info font-medium">{selectedStock.categoria}</p>
              </div>
            </div>
          )}

          {/* 2. Quantity & Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-ink mb-1">
                Cantidad a Usar <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 text-lg font-semibold focus:border-danger focus:outline-none focus:ring-2 focus:ring-danger/20"
                  value={formData.cantidad ?? ''}
                  onChange={(e) => {
                    const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                    setFormData({ ...formData, cantidad: newValue });
                  }}
                  min="0.01"
                  step="0.01"
                  max={selectedStock?.cantidad || undefined}
                  required
                  disabled={!formData.stock_id}
                  placeholder="0"
                />
                {selectedStock && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-sage">{selectedStock.unidad_medida}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 3. Date & Note */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-ink mb-1">
                Fecha
              </label>
              <input
                type="date"
                className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink focus:border-danger focus:outline-none focus:ring-2 focus:ring-danger/20"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                max={getLocalISODate()}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-1">
                Nota (Opcional)
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 focus:border-danger focus:outline-none focus:ring-2 focus:ring-danger/20"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Detalles..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-hairline">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 text-ink bg-paper-2 rounded-xl hover:bg-hairline/50 transition-colors font-medium"
                disabled={loading}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !formData.stock_id}
              className={`px-8 py-3 text-paper rounded-xl shadow-card transform transition-all hover:scale-105 font-bold text-lg flex items-center space-x-2 ${
                loading || !formData.stock_id ? 'bg-hairline cursor-not-allowed' : 'bg-danger hover:bg-danger/80'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-paper"></div>
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <span>📉</span>
                  <span>Confirmar Consumo</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
      {/* Error Modal - same as LedgerModal */}
      <ConfirmationModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => setErrorModalOpen(false)}
        title="Error de Validación"
        message={errorMessage}
        confirmText="Entendido"
        cancelText=""
        isDestructive={false}
      />
    </div>
  );
};
